import ModalConfirmDelete from "@/components/modal-confirm";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { StudentNoteService } from "@/services/api/student-note";
import { StudentNote } from "@/utils/entities";
import { getToastErrorContent } from "@/utils/functions/api-error";
import { Loader2Icon, PencilIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import ModalEditStudentNote from "./modal-edit-student-note";

type Props = {
  studentId: string;
  notes?: StudentNote[];
  isLoading?: boolean;
  emptyMessage?: string;
  variant?: "dark" | "light";
  onDeleted?: () => void;
};

function getAuthorName(note: StudentNote) {
  return (
    note.author?.full_name ||
    [note.author?.name, note.author?.surname].filter(Boolean).join(" ") ||
    "Usuário"
  );
}

export default function StudentNotesList({
  studentId,
  notes = [],
  isLoading = false,
  emptyMessage = "Nenhuma anotação registrada.",
  variant = "dark",
  onDeleted,
}: Props) {
  const isLight = variant === "light";
  const { toast } = useToast();
  const [noteToDelete, setNoteToDelete] = useState<StudentNote | null>(null);
  const [noteToEdit, setNoteToEdit] = useState<StudentNote | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  async function handleConfirmDelete() {
    if (!noteToDelete) return;

    try {
      setIsDeleting(true);

      await StudentNoteService.delete({
        studentId,
        noteId: noteToDelete.id,
      });

      toast({
        variant: "success",
        description: "Anotação excluída com sucesso.",
      });

      setNoteToDelete(null);
      onDeleted?.();
    } catch (error) {
      toast(getToastErrorContent(error));
    } finally {
      setIsDeleting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2Icon className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!notes.length) {
    return (
      <p
        className={`text-sm py-4 ${isLight ? "text-gray-500" : "text-gray-400"}`}
      >
        {emptyMessage}
      </p>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        {notes.map((note) => (
          <div
            key={note.id}
            className={
              isLight
                ? "rounded-md border border-gray-200 bg-gray-50 p-4 text-gray-800"
                : "rounded-md border border-gray-600 bg-gray-700/50 p-4 text-gray-200"
            }
          >
            <div className="flex items-start justify-between gap-3">
              <p className="whitespace-pre-wrap text-sm flex-1">
                {note.content}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className={
                    isLight
                      ? "shrink-0 text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                      : "shrink-0 text-gray-400 hover:text-gray-200 hover:bg-gray-600"
                  }
                  onClick={() => setNoteToEdit(note)}
                >
                  <PencilIcon size={16} />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className={
                    isLight
                      ? "shrink-0 text-gray-500 hover:text-red-600 hover:bg-red-50"
                      : "shrink-0 text-gray-400 hover:text-red-400 hover:bg-gray-600"
                  }
                  onClick={() => setNoteToDelete(note)}
                >
                  <Trash2Icon size={16} />
                </Button>
              </div>
            </div>
            <div
              className={`mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs ${
                isLight ? "text-gray-500" : "text-gray-400"
              }`}
            >
              <span>{note.createdAtText}</span>
              <span>por {getAuthorName(note)}</span>
            </div>
          </div>
        ))}
      </div>

      <ModalConfirmDelete
        isVisible={!!noteToDelete}
        isLoading={isDeleting}
        onCancel={() => !isDeleting && setNoteToDelete(null)}
        onContinue={handleConfirmDelete}
        description={
          <>
            Isso irá <strong>excluir</strong> esta anotação permanentemente.
          </>
        }
      />

      <ModalEditStudentNote
        studentId={studentId}
        note={noteToEdit}
        isOpen={!!noteToEdit}
        onClose={() => setNoteToEdit(null)}
        onSuccess={() => onDeleted?.()}
      />
    </>
  );
}
